import React, { useRef, useState } from 'react';
import { Button, Input, Upload, message } from 'antd';
import { CloseOutlined, PaperClipOutlined, SendOutlined } from '@ant-design/icons';

const { TextArea } = Input;

function MessageInput({ onSend, disabled, supportsImage, sessionId }) {
  const [content, setContent] = useState('');
  const [imageIds, setImageIds] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const imageIdsRef = useRef([]);

  const handleSend = () => {
    if (content.trim() && !disabled && !uploading) {
      onSend(content, imageIdsRef.current);
      setContent('');
      imageIdsRef.current = [];
      setImageIds([]);
      setFileList([]);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const uploadProps = {
    accept: 'image/*',
    action: '/api/chat/images',
    data: { sessionId },
    name: 'image',
    showUploadList: false,
    fileList,
    withCredentials: true,
    beforeUpload: () => {
      setUploading(true);
      return true;
    },
    onChange: info => {
      const nextFileList = info.fileList || [];
      setFileList(nextFileList);
      if (info.file.status === 'done') {
        setImageIds(ids => {
          const nextIds = [...ids, info.file.response.id];
          imageIdsRef.current = nextIds;
          return nextIds;
        });
        message.success('图片已上传');
      } else if (info.file.status === 'error') {
        message.error(info.file.response?.error || '图片上传失败');
      }
      // 以整个列表是否还有"上传中"文件为准来决定锁定状态，
      // 避免个别边缘状态（无 status / 被中断）导致 uploading 永久卡死、发送键灰死
      const stillUploading = nextFileList.some(f => f.status === 'uploading');
      setUploading(stillUploading);
    },
    onRemove: file => {
      if (file.response?.id) {
        setImageIds(ids => {
          const nextIds = ids.filter(id => id !== file.response.id);
          imageIdsRef.current = nextIds;
          return nextIds;
        });
      }
    }
  };

  const removeFile = (file) => {
    setFileList(files => {
      const nextFiles = files.filter(item => item.uid !== file.uid);
      setUploading(nextFiles.some(f => f.status === 'uploading'));
      return nextFiles;
    });
    if (file.response?.id) {
      setImageIds(ids => {
        const nextIds = ids.filter(id => id !== file.response.id);
        imageIdsRef.current = nextIds;
        return nextIds;
      });
    }
  };

  return (
    <div className="message-composer">
      {fileList.length > 0 && (
        <div className="composer-attachments">
          {fileList.map(file => (
            <div className="composer-attachment" key={file.uid}>
              <PaperClipOutlined />
              <span className="composer-attachment-name">{file.name}</span>
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined />}
                onClick={() => removeFile(file)}
              />
            </div>
          ))}
        </div>
      )}
      <div className="composer-row">
        {supportsImage && (
          <Upload {...uploadProps}>
            <Button
              className="composer-icon-button"
              icon={<PaperClipOutlined />}
              disabled={disabled || uploading}
            />
          </Upload>
        )}
        <TextArea
          className="composer-textarea"
          value={content}
          onChange={e => setContent(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="输入消息..."
          autoSize={{ minRows: 1, maxRows: 4 }}
          disabled={disabled}
        />
        <Button
          className="composer-send-button"
          type="primary"
          icon={<SendOutlined />}
          onClick={handleSend}
          disabled={disabled || uploading || !content.trim()}
        />
      </div>
    </div>
  );
}

export default MessageInput;
